import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Wand2, Download, QrCode, Users, Settings, X, UserPlus, Trash2,
  Image as ImageIcon, Tag, Save, Plus, ExternalLink,
} from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const resolve = (u) => (u && u.startsWith('/api') ? `${API.replace('/api', '')}${u}` : u);

// ============ Manage Org Modal (branding + admins + categories) ============
const ManageOrgModal = ({ org, onClose, onRefresh }) => {
  const [tab, setTab] = useState('branding');
  const [branding, setBranding] = useState(org.branding || {});
  const [admins, setAdmins] = useState(org.admins || []);
  const [categories, setCategories] = useState(org.categories || []);
  const [newAdmin, setNewAdmin] = useState('');
  const [newCatKey, setNewCatKey] = useState('');
  const [newCatLabel, setNewCatLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const saveBranding = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/organizations/${org.org_id}/branding`, branding);
      setBranding(res.data.branding);
      toast.success('Branding mis a jour');
      onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBranding({ ...branding, [field]: res.data.url });
    } catch { toast.error('Upload impossible'); }
    finally { setUploading(false); }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.trim()) return;
    try {
      const res = await axios.post(`${API}/organizations/${org.org_id}/admins`, { email: newAdmin.trim() });
      setAdmins([...admins, res.data.admin]);
      setNewAdmin('');
      toast.success('Admin ajoute');
      onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const removeAdmin = async (userId, email) => {
    if (!window.confirm(`Retirer ${email} des admins ?`)) return;
    try {
      await axios.delete(`${API}/organizations/${org.org_id}/admins/${userId}`);
      setAdmins(admins.filter((a) => a.user_id !== userId));
      toast.success('Admin retire');
      onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const addCategory = () => {
    const key = newCatKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const label = newCatLabel.trim();
    if (!key || !label) return toast.error('Cle et libelle requis');
    if (categories.some((c) => c.key === key)) return toast.error('Cle deja utilisee');
    setCategories([...categories, { key, label }]);
    setNewCatKey('');
    setNewCatLabel('');
  };

  const removeCategory = (key) => setCategories(categories.filter((c) => c.key !== key));

  const saveCategories = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/organizations/${org.org_id}/categories`, { categories });
      setCategories(res.data.categories);
      toast.success('Categories enregistrees');
      onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="manage-org-modal">
        <div className="flex items-center justify-between p-4 border-b-2 border-charcoal/10 sticky top-0 bg-white">
          <h3 className="font-heading font-bold text-lg">Gerer - {org.name}</h3>
          <button onClick={onClose} className="p-1 hover:bg-surface-alt rounded-lg" data-testid="manage-org-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-3 border-b border-charcoal/10">
          {[
            { id: 'branding', label: 'Branding', icon: ImageIcon },
            { id: 'admins', label: 'Admins', icon: Users },
            { id: 'categories', label: 'Categories', icon: Tag },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-t-lg border-b-2 ${
                tab === id ? 'border-primary text-charcoal' : 'border-transparent text-charcoal/50 hover:text-charcoal'
              }`}
              data-testid={`manage-tab-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-3">
          {tab === 'branding' && (
            <div className="space-y-3" data-testid="manage-branding-panel">
              <p className="text-xs text-charcoal/60">Ces informations s'affichent lorsqu'un PingMe de cette organisation est scanne.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-charcoal/70 mb-1 block">Nom affiche</label>
                  <input className="input-brutal w-full" value={branding.display_name || ''} onChange={(e) => setBranding({ ...branding, display_name: e.target.value })} data-testid="manage-display-name" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-charcoal/70 mb-1 block">Couleur primaire</label>
                  <div className="flex gap-2">
                    <input type="color" value={branding.primary_color || '#FF6B35'} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="w-10 h-10 border-2 border-charcoal rounded-lg" />
                    <input className="input-brutal flex-1" value={branding.primary_color || ''} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} placeholder="#FF6B35" data-testid="manage-primary-color" />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-charcoal/70 mb-1 block">Tagline</label>
                <input className="input-brutal w-full" value={branding.tagline || ''} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} data-testid="manage-tagline" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-charcoal/70 mb-1 block">Message complet</label>
                <textarea className="input-brutal w-full" rows={3} value={branding.message || ''} onChange={(e) => setBranding({ ...branding, message: e.target.value })} data-testid="manage-message" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-charcoal/70 mb-1 block">URL site</label>
                <input className="input-brutal w-full" value={branding.website_url || ''} onChange={(e) => setBranding({ ...branding, website_url: e.target.value })} placeholder="https://..." data-testid="manage-website" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-charcoal/70 mb-1 block">Logo</label>
                  <div className="flex items-center gap-2">
                    {branding.logo_url && <img src={resolve(branding.logo_url)} alt="logo" className="w-10 h-10 rounded-lg border-2 border-charcoal object-cover" />}
                    <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0], 'logo_url')} className="text-xs" data-testid="manage-logo-upload" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-charcoal/70 mb-1 block">Image de couverture</label>
                  <div className="flex items-center gap-2">
                    {branding.cover_image_url && <img src={resolve(branding.cover_image_url)} alt="cover" className="h-10 rounded-lg border-2 border-charcoal object-cover" />}
                    <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0], 'cover_image_url')} className="text-xs" data-testid="manage-cover-upload" />
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-2 p-2 rounded-lg hover:bg-charcoal/5 border border-charcoal/10">
                <input
                  type="checkbox"
                  checked={branding.embed_qr_logo !== false}
                  onChange={(e) => setBranding({ ...branding, embed_qr_logo: e.target.checked })}
                  data-testid="manage-embed-qr-logo"
                />
                <span>
                  <span className="font-bold">Integrer le logo dans le QR code</span>
                  <span className="block text-xs text-charcoal/60">Le logo apparaitra au centre des futurs QR generes (correction d'erreur H).</span>
                </span>
              </label>
              <button onClick={saveBranding} disabled={saving || uploading} className="btn-primary text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="manage-save-branding">
                <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Enregistrer le branding'}
              </button>
            </div>
          )}

          {tab === 'admins' && (
            <div className="space-y-3" data-testid="manage-admins-panel">
              <p className="text-xs text-charcoal/60">Les admins peuvent gerer le branding, les categories, generer des PingMes et ajouter d'autres admins.</p>
              {admins.length > 0 ? (
                <ul className="divide-y divide-charcoal/10 border border-charcoal/10 rounded-xl overflow-hidden">
                  {admins.map((a) => (
                    <li key={a.user_id} className="flex items-center justify-between px-3 py-2 bg-white">
                      <div className="min-w-0">
                        <p className="text-sm font-bold truncate">{a.name || a.email}</p>
                        <p className="text-xs text-charcoal/50 truncate">{a.email}</p>
                      </div>
                      <button onClick={() => removeAdmin(a.user_id, a.email)} className="text-danger hover:bg-danger/10 p-1.5 rounded-lg" data-testid={`manage-remove-admin-${a.user_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-charcoal/50 italic">Aucun admin</p>
              )}
              <form onSubmit={addAdmin} className="flex gap-2">
                <input type="email" className="input-brutal flex-1" placeholder="email@exemple.com" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} required data-testid="manage-new-admin-email" />
                <button type="submit" className="btn-primary text-sm px-4 py-2 flex items-center gap-2" data-testid="manage-add-admin-btn">
                  <UserPlus className="w-4 h-4" /> Ajouter
                </button>
              </form>
              <p className="text-[10px] text-charcoal/50">L'utilisateur doit deja avoir un compte sur Tagora.</p>
            </div>
          )}

          {tab === 'categories' && (
            <div className="space-y-3" data-testid="manage-categories-panel">
              <p className="text-xs text-charcoal/60">Definissez les categories d'objets que les utilisateurs pourront choisir en enrolant un PingMe. Laisser vide = categories par defaut.</p>
              {categories.length > 0 ? (
                <ul className="divide-y divide-charcoal/10 border border-charcoal/10 rounded-xl overflow-hidden">
                  {categories.map((c) => (
                    <li key={c.key} className="flex items-center justify-between px-3 py-2 bg-white">
                      <div className="min-w-0 flex items-center gap-2">
                        <span className="text-xs font-mono bg-charcoal/5 px-2 py-0.5 rounded">{c.key}</span>
                        <span className="text-sm font-bold">{c.label}</span>
                      </div>
                      <button onClick={() => removeCategory(c.key)} className="text-danger hover:bg-danger/10 p-1.5 rounded-lg" data-testid={`manage-remove-cat-${c.key}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-charcoal/50 italic">Aucune categorie personnalisee (categories par defaut utilisees)</p>
              )}
              <div className="grid grid-cols-[1fr_2fr_auto] gap-2">
                <input className="input-brutal" placeholder="cle (a-z, 0-9, _)" value={newCatKey} onChange={(e) => setNewCatKey(e.target.value)} data-testid="manage-new-cat-key" />
                <input className="input-brutal" placeholder="Libelle affiche" value={newCatLabel} onChange={(e) => setNewCatLabel(e.target.value)} data-testid="manage-new-cat-label" />
                <button type="button" onClick={addCategory} className="btn-outline text-sm px-3 py-2 flex items-center gap-1" data-testid="manage-add-cat-btn">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button onClick={saveCategories} disabled={saving} className="btn-primary text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="manage-save-categories">
                <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Enregistrer les categories'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ Compact OrgRow with inline generate + manage button ============
const OrgRow = ({ org, onRefresh }) => {
  const [showGenerate, setShowGenerate] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [count, setCount] = useState(1);
  const [label, setLabel] = useState('perdu ? Scannez moi');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState([]);
  const navigate = useNavigate();

  const max = org.max_pingmes || 100000;
  const used = org.pingme_count || 0;
  const pct = Math.min(100, Math.round((used / max) * 100));

  const submitGen = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setGenerated([]);
    try {
      const res = await axios.post(`${API}/organizations/${org.org_id}/generate-pingmes`, {
        count: Number(count) || 1,
        label_text: label || undefined,
      });
      setGenerated(res.data.generated || []);
      toast.success(`${res.data.count} PingMe(s) genere(s)`);
      onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setGenerating(false); }
  };

  return (
    <>
      <div className="bg-white border-2 border-charcoal rounded-xl p-3" data-testid={`my-org-card-${org.org_id}`}>
        {/* Compact header row */}
        <div className="flex items-center gap-2.5">
          {org.branding?.logo_url ? (
            <img src={resolve(org.branding.logo_url)} alt="" className="w-9 h-9 rounded-lg border border-charcoal object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-lg border border-charcoal flex items-center justify-center shrink-0" style={{ backgroundColor: org.branding?.primary_color || '#FF6B35' }}>
              <Building2 className="w-4 h-4 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-bold text-sm text-charcoal truncate leading-tight">{org.name}</p>
              {org.use_case === 'partner' && (
                <span className="bg-primary/30 text-charcoal text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-charcoal/30 tracking-widest uppercase flex-shrink-0">
                  Partenaire
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-charcoal/50">
              <span>{used}/{max}</span>
              <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" /> {(org.admins || []).length}</span>
              <span className="flex items-center gap-0.5"><Tag className="w-2.5 h-2.5" /> {(org.categories || []).length}</span>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setShowGenerate(!showGenerate)}
              className="p-1.5 rounded-lg border border-charcoal/30 hover:bg-primary/20"
              title="Generer des PingMes"
              data-testid={`my-org-generate-btn-${org.org_id}`}
            >
              <Wand2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => {
                if (org.use_case === 'partner') {
                  navigate('/partners/dashboard');
                } else {
                  setShowManage(true);
                }
              }}
              className="p-1.5 rounded-lg border border-charcoal/30 hover:bg-surface-alt"
              title={org.use_case === 'partner' ? "Acceder a l'espace partenaire" : "Gerer l'organisation"}
              data-testid={`my-org-manage-btn-${org.org_id}`}
            >
              {org.use_case === 'partner' ? <ExternalLink className="w-3.5 h-3.5" /> : <Settings className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* Thin quota bar */}
        <div className="h-1 bg-charcoal/10 rounded-full overflow-hidden mt-2">
          <div className={`h-full ${pct > 90 ? 'bg-danger' : pct > 70 ? 'bg-orange-400' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
        </div>

        {/* Inline generate panel */}
        {showGenerate && (
          <form onSubmit={submitGen} className="mt-3 pt-3 border-t border-charcoal/10 space-y-2" data-testid={`my-org-generate-form-${org.org_id}`}>
            <div className="grid grid-cols-[80px_1fr_auto] gap-2">
              <input type="number" min="1" max="500" className="input-brutal text-sm" value={count} onChange={(e) => setCount(e.target.value)} data-testid={`my-org-count-${org.org_id}`} />
              <input className="input-brutal text-sm" placeholder="Texte QR" value={label} onChange={(e) => setLabel(e.target.value)} data-testid={`my-org-label-${org.org_id}`} />
              <button type="submit" disabled={generating} className="btn-primary text-xs px-3 disabled:opacity-50 flex items-center gap-1" data-testid={`my-org-submit-${org.org_id}`}>
                <Wand2 className="w-3 h-3" /> {generating ? '...' : 'OK'}
              </button>
            </div>
            {generated.length > 0 && (
              <div className="border border-green-300 bg-green-50 rounded-lg p-2" data-testid={`my-org-generated-${org.org_id}`}>
                <p className="text-xs font-bold text-green-800 mb-1.5">{generated.length} PingMe(s) :</p>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-40 overflow-y-auto">
                  {generated.map((g) => (
                    <div key={g.pingme_id} className="bg-white border border-charcoal/20 rounded p-1 text-center">
                      <img src={`data:image/png;base64,${g.qr_code_base64}`} alt={g.pingme_id} className="w-full aspect-square object-contain" />
                      <a href={`data:image/png;base64,${g.qr_code_base64}`} download={`${g.pingme_id}.png`} className="inline-flex items-center gap-0.5 text-[8px] text-primary underline">
                        <Download className="w-2 h-2" /> PNG
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}
      </div>

      {showManage && <ManageOrgModal org={org} onClose={() => setShowManage(false)} onRefresh={onRefresh} />}
    </>
  );
};

const MyOrganizationsSection = ({ hideHeader = false } = {}) => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/organizations/my`);
      setOrgs(res.data || []);
    } catch { /* user probably not an org admin */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  if (loading || orgs.length === 0) return null;

  return (
    <section className="mb-6" data-testid="my-organizations-section">
      {!hideHeader && (
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-charcoal text-white w-7 h-7 rounded-lg flex items-center justify-center">
            <Building2 className="w-3.5 h-3.5" />
          </div>
          <h2 className="font-heading font-bold text-base">Mes organisations</h2>
          <span className="text-xs text-charcoal/50">({orgs.length})</span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {orgs.map((org) => (
          <OrgRow key={org.org_id} org={org} onRefresh={fetchOrgs} />
        ))}
      </div>
    </section>
  );
};

export default MyOrganizationsSection;
